import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import CurrencyText from "../../../../../components/fragment/cur-text/CurrencyText";
import { RazorCheckBox, RazorToggleSlide } from "../../../../../components/razor-reuse";
import { ThirdPartyVendorControl } from "../../../../../redux/type";
import {
  buildVendorEditState,
  vendorDisplayName,
  type VendorEditState,
} from "../vendorControlHelpers";
import CurrencyToggleRow from "./CurrencyToggleRow";

interface VendorCardProps {
  vendor: ThirdPartyVendorControl;
  isEditing: boolean;
  isSaving: boolean;
  onStartEdit: (vendorId: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: (
    vendor: ThirdPartyVendorControl,
    editState: VendorEditState,
  ) => Promise<boolean>;
  onDelete: (vendor: ThirdPartyVendorControl) => void;
}

export default function VendorCard({
  vendor,
  isEditing,
  isSaving,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
}: VendorCardProps) {
  const [editState, setEditState] = useState<VendorEditState | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Read-only rows render straight off the vendor record; while editing they render off local state.
  const view = isEditing && editState ? editState : buildVendorEditState(vendor);
  const supportedCurrencies = (vendor?.supported_currencies || []).filter(Boolean);

  const handleStartEdit = () => {
    setEditState(buildVendorEditState(vendor));
    onStartEdit(vendor.id);
  };

  const handleCancel = () => {
    setEditState(null);
    onCancelEdit();
  };

  const handleSave = async () => {
    if (!editState) return;
    // Only clear the local edit and drop out of edit mode once the server
    // actually accepted the change — a failed save shouldn't lose the
    // admin's in-progress toggles.
    const success = await onSaveEdit(vendor, editState);
    if (success) {
      setEditState(null);
    }
  };

  const updateEditState = (patch: Partial<VendorEditState>) => {
    setEditState((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const toggleCurrency = (
    field: "business" | "direct",
    code: string,
    next: boolean,
  ) => {
    setEditState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        currencies: prev.currencies.map((entry) =>
          entry.code === code ? { ...entry, [field]: next } : entry,
        ),
      };
    });
  };

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
      <div className="flex items-center justify-between">
        <p className="text-[16px] font-bold text-[#1E1E1E]">
          {vendorDisplayName(vendor)}
        </p>
        <div className="flex items-center gap-[8px]">
          {!isEditing && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((prev) => !prev)}
                className="flex size-[32px] items-center justify-center rounded-full bg-[#F4F4F5] text-[#758089]"
              >
                <MoreHorizontal className="size-[16px]" />
              </button>
              {menuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-[38px] z-20 w-[170px] rounded-[8px] border border-[#E4E4E7] bg-white py-[6px] shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        onDelete(vendor);
                      }}
                      className="flex w-full items-center gap-[8px] px-[14px] py-[8px] text-[13px] text-[#DC2626] hover:bg-[#FEF2F2]"
                    >
                      <Trash2 className="size-[14px]" />
                      Delete Vendor
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {isEditing ? (
            <>
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSaving}
                className="rounded-[8px] border border-[#E4E4E7] px-[14px] py-[8px] text-[13px] font-medium text-[#3F3F46] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="rounded-[8px] bg-[#734792] px-[14px] py-[8px] text-[13px] font-medium text-white disabled:opacity-50"
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleStartEdit}
              className="inline-flex items-center gap-[6px] rounded-[8px] border border-[#734792] px-[14px] py-[8px] text-[13px] font-medium text-[#734792]"
            >
              <Pencil className="size-[14px]" />
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="mt-[16px] flex items-center gap-[24px] border-b border-[#F0F0F0] pb-[16px]">
        <label className="flex items-center gap-[8px]">
          <RazorCheckBox
            checked={view.applies_to_mobile}
            disabled={!isEditing}
            onChange={(e) => updateEditState({ applies_to_mobile: e.target.checked })}
            color="black-light"
            id={`${vendor.id}-applies_to_mobile`}
          />
          <span className="text-[14px] text-[#1E1E1E]">Support Mobile</span>
        </label>
        <label className="flex items-center gap-[8px]">
          <RazorCheckBox
            checked={view.applies_to_web}
            disabled={!isEditing}
            onChange={(e) => updateEditState({ applies_to_web: e.target.checked })}
            color="black-light"
            id={`${vendor.id}-applies_to_web`}
          />
          <span className="text-[14px] text-[#1E1E1E]">Support Web</span>
        </label>
      </div>

      <div className="mt-[16px] flex flex-wrap items-center gap-x-[32px] gap-y-[12px] border-b border-[#F0F0F0] pb-[16px]">
        <div className="flex items-center gap-[8px]">
          <span className="text-[13px] text-[#758089]">Mobile Active</span>
          <RazorToggleSlide
            checked={view.mobile_is_active}
            disabled={!isEditing}
            onChange={(e: any) =>
              updateEditState({ mobile_is_active: e.target.checked })
            }
            color="black-light"
            id={`${vendor.id}-mobile_is_active`}
          />
        </div>
        <div className="flex items-center gap-[8px]">
          <span className="text-[13px] text-[#758089]">Web Active</span>
          <RazorToggleSlide
            checked={view.web_is_active}
            disabled={!isEditing}
            onChange={(e: any) =>
              updateEditState({ web_is_active: e.target.checked })
            }
            color="black-light"
            id={`${vendor.id}-web_is_active`}
          />
        </div>
        <div className="flex items-center gap-[8px]">
          <span className="text-[13px] text-[#758089]">Vendor Active</span>
          <RazorToggleSlide
            checked={view.is_active}
            disabled={!isEditing}
            onChange={(e: any) => updateEditState({ is_active: e.target.checked })}
            color="black-light"
            id={`${vendor.id}-is_active`}
          />
        </div>
      </div>

      {supportedCurrencies.length > 0 && (
        <div className="mt-[16px]">
          <p className="text-[13px] font-medium text-[#1E1E1E]">
            Supported Currencies
          </p>
          <div className="mt-[8px] flex flex-wrap gap-[8px]">
            {supportedCurrencies.map((code) => (
              <span
                key={code}
                className="inline-flex items-center gap-[6px] rounded-full border border-[#E4E4E7] px-[10px] py-[4px] text-[12px] text-[#3F3F46]"
              >
                <CurrencyText currency={code} text={code.toUpperCase()} imgSize="16px" />
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-[16px] space-y-[16px]">
        <CurrencyToggleRow
          idPrefix={vendor.id}
          label="Business Users"
          field="business"
          currencies={view.currencies}
          isEditing={isEditing}
          onToggle={(code, next) => toggleCurrency("business", code, next)}
        />
        <CurrencyToggleRow
          idPrefix={vendor.id}
          label="Direct Users"
          field="direct"
          currencies={view.currencies}
          isEditing={isEditing}
          onToggle={(code, next) => toggleCurrency("direct", code, next)}
        />
      </div>
    </div>
  );
}
